import React, { useState, useEffect, useRef } from 'react';

interface JobSource {
  id: number;
  name: string;
  display_name: string;
}

interface JobSourcesFilterDropdownProps {
  show: boolean;
  onHide: () => void;
  onApply: (jobSourceIds: number[]) => void;
  selectedJobSourceIds: number[];
  buttonRef: React.RefObject<HTMLButtonElement>;
}

const JobSourcesFilterDropdown: React.FC<JobSourcesFilterDropdownProps> = ({
  show,
  onHide,
  onApply,
  selectedJobSourceIds,
  buttonRef
}) => {
  const [jobSources, setJobSources] = useState<JobSource[]>([]);
  const [filteredJobSources, setFilteredJobSources] = useState<JobSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>(selectedJobSourceIds);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedIds(selectedJobSourceIds);
  }, [selectedJobSourceIds]);

  useEffect(() => {
    if (show) {
      fetchJobSources();
    }
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        onHide();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onHide, buttonRef]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredJobSources(jobSources);
    } else {
      const filtered = jobSources.filter(js =>
        js.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        js.display_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredJobSources(filtered);
    }
  }, [searchQuery, jobSources]);

  const fetchJobSources = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/job_sources/for_search_filters');
      const data = await response.json();
      setJobSources(data);
      setFilteredJobSources(data);
    } catch (error) {
      console.error('Failed to fetch job sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJobSourceToggle = (jobSourceId: number) => {
    const newSelection = selectedIds.includes(jobSourceId) 
      ? selectedIds.filter(id => id !== jobSourceId)
      : [...selectedIds, jobSourceId];
    
    setSelectedIds(newSelection);
    
    // エージェント選択時に自動的に検索を実行
    onApply(newSelection);
  };

  const handleApply = () => {
    onApply(selectedIds);
    onHide();
  };

  const handleClear = () => {
    setSelectedIds([]);
    setSearchQuery('');
    onApply([]);
  };

  if (!show) return null;

  return (
    <div 
      ref={dropdownRef}
      className="position-absolute bg-white border rounded shadow-lg" 
      style={{ 
        top: '100%', 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        maxHeight: '400px',
        overflowY: 'auto',
        minWidth: '500px'
      }}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">フリーランスエージェントで絞り込み</h6>
          <button type="button" className="btn-close" onClick={onHide}></button>
        </div>
        
        {/* 検索入力 */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="エージェント名で検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {loading ? (
          <div className="text-center">読み込み中...</div>
        ) : (
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {filteredJobSources.length === 0 ? (
              <div className="text-center text-muted">該当するエージェントが見つかりません</div>
            ) : (
              <div className="row">
                {filteredJobSources.map(jobSource => (
                  <div key={jobSource.id} className="col-md-6 mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`job-source-${jobSource.id}`}
                        checked={selectedIds.includes(jobSource.id)}
                        onChange={() => handleJobSourceToggle(jobSource.id)}
                      />
                      <label className="form-check-label" htmlFor={`job-source-${jobSource.id}`}>
                        {jobSource.display_name || jobSource.name}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClear}>
            クリア
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleApply}>
            適用
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobSourcesFilterDropdown;

